import _ from "lodash";
import moment from "moment";
import { normalizeBulletList } from "./encodingHelper";
import {
  formatDecimalForInput,
  getClientInfo,
  getPersonFirstLastName,
  normalizeDecimalSeparator,
} from "./formatters";
import { getName, formatOptionsTranslated } from "./staffHelper";
import {
  FILTERS_FOR_NAMES,
  MINWORDMISSIONDESCRIPTION,
  MISSION_STATUS_FILTERS,
  TAG_TYPES,
  REMOTE_WORK_TAGS,
  TARIFICATION_TYPE,
} from "constants/constants";

import { isAfter, parseISO, startOfDay } from "date-fns";

export const isAfterCalendarDay = ({ date, referenceDate }) =>
  !!date &&
  !!referenceDate &&
  isAfter(startOfDay(parseISO(date)), startOfDay(parseISO(referenceDate)));

export const extractBranchIds = (staffAssignedBranchWithoutScope) =>
  Object.values(staffAssignedBranchWithoutScope || {}).flat();

export const getAssignedBranches = (branchIds, branchList) =>
  branchList?.filter((branch) => branchIds.includes(branch.id)) || [];

export const selectOptimalBranch = (
  managerContractBranchId,
  assignedBranches
) => {
  if (!assignedBranches?.length) return null;

  const contractBranch = assignedBranches.find(
    (b) => b.id === managerContractBranchId
  );
  return contractBranch || assignedBranches[0];
};

export const formatBranchForForm = (branch) => ({
  value: branch.id,
  type: TAG_TYPES.BRANCHES,
  label: branch.name,
});

export const clearManagerBranches = ({ setValue, setManagerBranches }) => {
  setManagerBranches([]);
  setValue("signed_by_id", []);
};

export const updateManagerBranches = ({
  managerContractBranchId,
  staffAssignedBranchWithoutScope,
  branchList,
  setManagerBranches,
  setValue,
}) => {
  const branchIds = extractBranchIds(staffAssignedBranchWithoutScope);
  const assignedBranches = getAssignedBranches(branchIds, branchList);

  setManagerBranches(assignedBranches);

  const selectedBranch = selectOptimalBranch(
    managerContractBranchId,
    assignedBranches
  );
  if (selectedBranch) {
    setValue("signed_by_id", [formatBranchForForm(selectedBranch)]);
  }
};

export const formatReceivedData = ({ data, namesByFilters, worker_id }) => {
  return data?.map((item) => {
    const client = namesByFilters?.find(
      (entity) =>
        entity?.id === item?.client_id &&
        entity?.type === FILTERS_FOR_NAMES.CLIENT
    );

    const manager = namesByFilters?.find(
      (entity) =>
        entity?.id === item.manager_id &&
        entity?.type === FILTERS_FOR_NAMES.PERSON
    );

    const clientName = client?.full_name || item.name || "";
    const managerName = manager
      ? ` ${manager?.first_name} ${manager?.last_name}`
      : "";

    return {
      ...item,
      client: clientName,
      manager: managerName,
      worker_id,
      isDeletedClient: client?.deleted,
    };
  });
};

export const getStringyfiedFilters = (filters) => {
  let result = _.chain(filters)
    .map((value, key) => {
      if (_.isNil(value) || (_.isString(value) && _.isEmpty(value))) {
        return "";
      } else if (_.isArray(value)) {
        return value?.length > 0 && value.map((v) => `&${key}=${v}`).join("");
      } else {
        return `&${key}=${value}`;
      }
    })
    .compact()
    .join("")
    .value();
  return result;
};

export const formatFilterIntoString = (filterArray) =>
  _.chain(filterArray).map("value").join(",").value().trim();

export const formatSubmittedData = (data) => {
  // The category is derived from the job and no longer stored on the mission
  // (redmine 12940). react-hook-form still registers the field — the select is
  // on screen to narrow the job list — so it has to be dropped explicitly,
  // otherwise the spread below puts it back in the body.
  const { domain_category_id, ...rest } = data ?? {};
  return {
    ...rest,
    adress: data?.adress,
    client_id: data?.client_id[0]?.value,
    domain_id: data?.domain_id[0]?.value,
    manager_id: Number(data?.manager_id[0]?.value),
    signed_by_id: data?.signed_by_id[0]?.value,
    worker_id: data?.worker_id[0]?.value,
    rate_coordinator: data?.rate_coordinator[0]?.value,
    remote_work_days: !_.isEmpty(data?.remote_work_days)
      ? _.join(data?.remote_work_days, ",")
      : null,
    charged_by_id: data?.charged_by_id[0]?.value,
    adv_id: data?.adv_id[0]?.value,
    mission_fee: parseFloat(data?.mission_fee),
    transport_fee: parseFloat(normalizeDecimalSeparator(data?.transport_fee)),
    restoration_fee: parseFloat(
      normalizeDecimalSeparator(data?.restoration_fee)
    ),
    is_confidential: !data?.is_visible,
    scheduled_end_date: data?.scheduled_end_date || null,
    end_date: data?.end_date || null,
  };
};

export const formatDisplayReceivedData = (
  data,
  { lang, branches, jobList, namesByFilters }
) => {
  return {
    ...data,
    description: normalizeBulletList(data?.description),
    manager_id: getPersonFirstLastName({
      list: namesByFilters,
      id: data?.manager_id,
    }),
    worker_id: getPersonFirstLastName({
      list: namesByFilters,
      id: data?.worker_id,
      type: TAG_TYPES.CSLT_STR,
    }),
    rate_coordinator: getPersonFirstLastName({
      list: namesByFilters,
      id: data?.rate_coordinator,
    }),
    signed_by_id: getName({
      list: branches,
      id: data?.signed_by_id,
      type: TAG_TYPES.BRANCHES,
    }),
    // Derived from the job by the cascade (redmine 12940): the mission no
    // longer stores its category, so the API does not return one. The field
    // starts empty and fills itself once the referential and the job are there.
    domain_category_id: "",
    domain_id: formatOptionsTranslated({
      id: Number(data?.domain_id),
      lang,
      list: jobList,
    }),
    client_id: getClientInfo({
      list: namesByFilters,
      id: data?.client_id,
    }),
    remote_work_days: _.split(data.remote_work_days, ","),
    charged_by_id: getName({
      list: branches,
      id: data?.charged_by_id,
      type: TAG_TYPES.BRANCHES,
    }),
    adv_id: getPersonFirstLastName({
      list: namesByFilters,
      id: data?.adv_id,
    }),
    transport_fee: formatDecimalForInput(data?.transport_fee, lang),
    restoration_fee: formatDecimalForInput(data?.restoration_fee, lang),
  };
};

export const formatImportedData = (
  data,
  { branches, namesByFilters, davFunctions, lang }
) => {
  const {
    name,
    charged_by_id,
    signed_by_id,
    manager_id,
    adv_id,
    rate_coordinator,
    currency_id,
    description,
    email,
    client_contact_email,
    contact_name,
    client_contact,
    address,
    postal_code,
    city,
    country,
    worker_id,
    domain_id,
    contract_sharepoint_url,
    client_contact_linkedin,
    client_id,
    client,
  } = data || {};

  const formattedData = {
    name,
    charged_by_id: getName({
      list: branches,
      id: charged_by_id,
      type: TAG_TYPES.BRANCHES,
    }),
    signed_by_id: getName({
      list: branches,
      id: signed_by_id,
      type: TAG_TYPES.BRANCHES,
    }),
    manager_id: getPersonFirstLastName({
      list: namesByFilters,
      id: manager_id,
    }),
    adv_id: getPersonFirstLastName({
      list: namesByFilters,
      id: adv_id,
    }),
    rate_coordinator: getPersonFirstLastName({
      list: namesByFilters,
      id: rate_coordinator,
    }),
    currency_id,
    description,
    client_contact_email: email || client_contact_email,
    client_contact: contact_name || client_contact,
    address: address
      ? [address.replace(/\r?\n/g, " "), postal_code + " " + city, country]
          .filter(Boolean)
          .join(", ")
      : address,
    worker_id: [
      {
        value: worker_id[0]?.value,
        type: TAG_TYPES.PEOPLE,
        label: worker_id[0]?.label,
      },
    ],
    // Derived from the job by the cascade (redmine 12940): the mission no
    // longer stores its category, so the API does not return one. The field
    // starts empty and fills itself once the referential and the job are there.
    domain_category_id: "",
    domain_id: davFunctions
      ?.filter((e) => e.id === domain_id)
      .map((e) => {
        return {
          label: lang === "fr" ? e.fr : e.en,
          value: e.id,
        };
      }),
    contract_sharepoint_url,
    client_contact_linkedin,
  };

  if (client_id) {
    formattedData.client_id = [
      {
        label: client || "",
        value: client_id,
        tag: TAG_TYPES.CLIENTS,
      },
    ];
  }

  return formattedData;
};

// `domain_category_id` is stripped for the same reason as in
// `formatSubmittedData`: the field is registered by the form but is not part of
// the mission any more.
export const formatPayloadToAdd = ({
  data: { domain_category_id, ...data } = {},
  isDetached = false,
}) => ({
  ...data,
  adv_id: data?.adv_id[0]?.value,
  charged_by_id: data?.charged_by_id[0]?.value,
  client_id: data?.client_id[0]?.value,
  domain_id: data?.domain_id[0]?.value,
  manager_id: Number(data?.manager_id[0]?.value),
  rate_coordinator: data?.rate_coordinator[0]?.value,
  remote_work_days: !_.isEmpty(data?.remote_work_days)
    ? _.join(data?.remote_work_days, ",")
    : null,
  signed_by_id: data?.signed_by_id[0]?.value,
  worker_id: data?.worker_id[0]?.value,
  mission_fee:
    data?.billing_type !== TARIFICATION_TYPE.OTHER
      ? Number(data?.mission_fee)
      : null,
  transport_fee: Number(normalizeDecimalSeparator(data?.transport_fee)),
  restoration_fee: Number(normalizeDecimalSeparator(data?.restoration_fee)),
  closed_at: "2017-04-24T20:29:37",
  status: "status_ok",
  is_confidential: !data?.is_visible,
  detach: isDetached,
  scheduled_end_date: data?.scheduled_end_date || null,
  end_date: data?.end_date || null,
});

export const getCorrespondingTag = ({ list, id }) =>
  _.chain(list)
    .filter((elm) => elm?.id === id)
    .first()
    .get("tag")
    .value();

export const formatMissionFeeToCreate = ({ data, missionId, managerId }) => ({
  mission_id: missionId,
  manager_id: managerId,
  establishment_date: data?.establishment_date,
  establishment_date_end: null,
  fee_value: data?.fee_value,
});

export const formatMissionManagerToCreate = ({
  data,
  missionId,
  managerId,
}) => ({
  mission_id: missionId,
  manager_id: managerId,
  establishment_date: data?.establishment_date,
  establishment_date_end: null,
});

export const getCurrentYearMonths = ({ selectedYear }) => {
  return _.range(1, 13).map((month) => {
    const formattedMonths = _.padStart(month.toString(), 2, "0");
    return `${formattedMonths}/${selectedYear}`;
  });
};

export const formatMissionBillsListToEdit = (data) =>
  data
    ?.filter((elm) => elm?.isUpdatedBill)
    .map((elm) => ({
      id: elm?.bill_id || elm?.id,
      mission_id: elm?.mission_id,
      bill_year: elm?.bill_year,
      bill_month: elm?.bill_month,
      bill_number: elm?.bill_number || null,
      turnover_scheduled: elm?.turnover_scheduled,
      to_bill_comment: elm?.to_bill_comment || null,
      turnover_billed: Number(elm?.turnover_billed) || null,
      to_bill: Number(elm?.to_bill) || null,
    }));

export const formatPartialMissionsWithBills = (data) => {
  const formatDate = (date) =>
    date ? moment(new Date(date)).format("YYYY-MM-DD") : null;
  return data?.map((e) => ({
    mission_partial: {
      id: e?.mission_id,
      name: e?.mission_name,
      client_id: e?.client_id,
      signed_by_id: e?.mission_signed_by_id,
      charged_by_id: e?.mission_charged_by_id,
      start_date: formatDate(e?.mission_start_date),
      end_date: formatDate(e?.mission_end_date),
      scheduled_end_date: formatDate(e?.mission_scheduled_end_date),
    },
    bills: [
      {
        id: e?.bill_id,
        mission_id: e?.mission_id,
        bill_year: e?.bill_year,
        bill_month: e?.bill_month,
        bill_number: e?.bill_number || null,
        turnover_scheduled: e?.turnover_scheduled,
        turnover_billed: Number(e.turnover_billed) || null,
        to_bill: Number(e?.to_bill) || null,
        to_bill_comment: e?.to_bill_comment || null,
      },
    ],
  }));
};

export const sortVisibleColumns = (list) =>
  _.chain(list).sortBy("index").value();

export const countWords = (str) => {
  if (!str) return 0;
  const words = str?.trim().split(/\s+/);
  return words?.length;
};

export const isMissionDescriptionTooSmall = (description) =>
  countWords(description) < MINWORDMISSIONDESCRIPTION;

export const getMissionFiltersStatusFormatted = (statusArr) =>
  MISSION_STATUS_FILTERS.filter((elm) => statusArr?.includes(elm.value));

export const isValueEmpty = (value) => {
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === "object" && value !== null) {
    return Object.keys(value).length === 0;
  }
  return value === "" || value === null || value === undefined;
};

export const getGeneralInfoRequiredFields = (dataTTV = null) => {
  const baseFields = [
    { field: "name", check: (watch) => !watch("name") },
    { field: "type_", check: (watch) => !watch("type_") },
    { field: "domain_id", check: (watch) => !watch("domain_id")?.length },
    { field: "manager_id", check: (watch) => !watch("manager_id") },
    { field: "signed_by_id", check: (watch) => !watch("signed_by_id") },
    { field: "worker_id", check: (watch) => !watch("worker_id")?.length },
    { field: "start_date", check: (watch) => !watch("start_date") },
    { field: "description", check: (watch) => !watch("description") },
    { field: "client_id", check: (watch) => !watch("client_id")?.length },
    {
      field: "client_contact_email",
      check: (watch) => !watch("client_contact_email"),
    },
    {
      field: "client_contact_linkedin",
      check: (watch) => !watch("client_contact_linkedin"),
    },
    { field: "address", check: (watch) => !watch("address") },
    { field: "remote_work_id", check: (watch) => !watch("remote_work_id") },
    { field: "computer", check: (watch) => watch("computer") === null },
    { field: "telephone", check: (watch) => watch("telephone") === null },
    { field: "is_star", check: (watch) => watch("is_star") === null },
    { field: "is_visible", check: (watch) => watch("is_visible") === null },
    { field: "impact", check: (watch) => watch("impact") === null },
  ];
  if (dataTTV) {
    baseFields.push(
      {
        field: "remote_work_days",
        check: (watch) => {
          const remoteWorkId = watch("remote_work_id");
          const tag = getCorrespondingTag({ list: dataTTV, id: remoteWorkId });
          return (
            tag === REMOTE_WORK_TAGS.CLIENT_IMPOSED &&
            (!watch("remote_work_days") ||
              watch("remote_work_days")?.length === 0)
          );
        },
      },
      {
        field: "non_remote_days_number",
        check: (watch) => {
          const remoteWorkId = watch("remote_work_id");
          const tag = getCorrespondingTag({ list: dataTTV, id: remoteWorkId });
          return (
            tag === REMOTE_WORK_TAGS.VOLUNTARY &&
            !watch("non_remote_days_number")
          );
        },
      }
    );
  }

  return baseFields;
};

export const getBillingRequiredFields = ({ isEdition = false } = {}) => {
  const fields = [
    {
      field: "charged_by_id",
      check: (watch) => !watch("charged_by_id")?.length,
    },
    { field: "currency_id", check: (watch) => !watch("currency_id") },
    { field: "billing_type", check: (watch) => !watch("billing_type") },
    {
      field: "mission_fee",
      check: (watch) =>
        watch("billing_type") !== TARIFICATION_TYPE.OTHER &&
        !watch("mission_fee"),
    },
    {
      field: "transport_fee",
      check: (watch) =>
        !watch("transport_fee") &&
        watch("transport_fee") !== 0 &&
        !watch("transport_fee_comment"),
    },
    {
      field: "transport_fee_comment",
      check: (watch) =>
        !watch("transport_fee") &&
        watch("transport_fee") !== 0 &&
        !watch("transport_fee_comment"),
    },
    {
      field: "restoration_fee",
      check: (watch) =>
        !watch("restoration_fee") &&
        watch("restoration_fee") !== 0 &&
        !watch("restoration_fee_comment"),
    },
    {
      field: "restoration_fee_comment",
      check: (watch) =>
        !watch("restoration_fee") &&
        watch("restoration_fee") !== 0 &&
        !watch("restoration_fee_comment"),
    },
    {
      field: "contract_sharepoint_url",
      check: (watch) => !watch("contract_sharepoint_url"),
    },
  ];

  if (isEdition) {
    fields.push({
      field: "adv_id",
      check: (watch) => !watch("adv_id")?.length,
    });
  }

  return fields;
};

export const checkFieldErrors = (errors, watch, requiredFields) => {
  return requiredFields.some(
    ({ field, check }) => errors?.[field] || check(watch)
  );
};

export const GENERAL_INFO_FIELDS = [
  "name",
  "type_",
  "domain_id",
  "manager_id",
  "signed_by_id",
  "worker_id",
  "start_date",
  "end_date",
  "scheduled_end_date",
  "description",
  "client_id",
  "client_contact",
  "client_contact_email",
  "client_contact_phonenumber",
  "client_contact_linkedin",
  "address",
  "remote_work_id",
  "remote_work_days",
  "non_remote_days_number",
  "computer",
  "telephone",
  "is_star",
  "is_visible",
  "impact",
];

export const BILLING_FIELDS = [
  "charged_by_id",
  "currency_id",
  "billing_type",
  "mission_fee",
  "transport_fee",
  "transport_fee_comment",
  "restoration_fee",
  "restoration_fee_comment",
  "contract_sharepoint_url",
  "rate_coordinator",
  "adv_id",
];

export const determineSectionForErrors = ({
  errors,
  watch,
  SECTIONS,
  dataTTV = null,
  isEdition = false,
}) => {
  const errorFields = Object.keys(errors);

  const hasGeneralInfoErrors = errorFields.some((field) =>
    GENERAL_INFO_FIELDS.includes(field)
  );
  const hasBillingErrors = errorFields.some((field) =>
    BILLING_FIELDS.includes(field)
  );

  if (hasGeneralInfoErrors) {
    return SECTIONS.GENERAL_INFO;
  } else if (hasBillingErrors) {
    return SECTIONS.BILLING;
  }

  const generalInfoFields = getGeneralInfoRequiredFields(dataTTV);
  const billingFields = getBillingRequiredFields({ isEdition });

  const hasGeneralInfoRequiredErrors = checkFieldErrors(
    errors,
    watch,
    generalInfoFields
  );
  const hasBillingRequiredErrors = checkFieldErrors(
    errors,
    watch,
    billingFields
  );

  if (hasGeneralInfoRequiredErrors) {
    return SECTIONS.GENERAL_INFO;
  } else if (hasBillingRequiredErrors) {
    return SECTIONS.BILLING;
  }

  return null;
};

export const handleSwitchDataValidation = (
  watch,
  setError,
  switchDataNames
) => {
  switchDataNames.forEach((element) => {
    if (watch(element) === null) {
      setError(element, {
        type: "manual",
        message: "Ce champ est obligatoire",
      });
    }
  });
};

export const validateAllRequiredFields = ({
  watch,
  setError,
  switchDataNames,
  dataTTV = null,
  isEdition = false,
}) => {
  handleSwitchDataValidation(watch, setError, switchDataNames);

  const hasSwitchErrors = switchDataNames.some(
    (switchName) => watch(switchName) === null
  );
  const hasGeneralInfoErrors = getGeneralInfoRequiredFields(dataTTV).some(
    ({ field, check }) => check(watch)
  );
  const hasBillingErrors = getBillingRequiredFields({ isEdition }).some(
    ({ field, check }) => check(watch)
  );

  return { hasSwitchErrors, hasGeneralInfoErrors, hasBillingErrors };
};

export const handleMissionValidationErrors = ({
  hasSwitchErrors,
  hasGeneralInfoErrors,
  setStatus,
  setCode,
  changeSection,
  setPendingValidation,
  POPUP_STATUS,
  FIELD_REQUIRED,
  SECTIONS,
}) => {
  setStatus(POPUP_STATUS.ERROR);
  setCode(FIELD_REQUIRED);

  const targetSection =
    hasSwitchErrors || hasGeneralInfoErrors
      ? SECTIONS.GENERAL_INFO
      : SECTIONS.BILLING;

  changeSection(targetSection);
  setPendingValidation(true);
};

export const submitMissionCreation = async ({
  info,
  managerId,
  personId,
  setDataPayload,
  isManagerOfPerson,
  formatPayloadToAdd,
  createMission,
  setIsOpen,
  setIsDetachmentModalOpen,
}) => {
  setDataPayload(info);
  const selectedManagerId = Number(managerId?.[0]?.value);
  const person = personId?.[0]?.value;

  const result = await isManagerOfPerson({
    personId: person,
    managerId: selectedManagerId,
  });
  const currentManagerId = result?.manager?.id;

  if (selectedManagerId === currentManagerId) {
    createMission(formatPayloadToAdd({ data: info, isDetached: false }));
    setIsOpen(true);
  } else {
    setIsDetachmentModalOpen(true);
  }
};

export const getDefaultMissionsFilters = ({ userFilterStorage }) => ({
  status: getMissionFiltersStatusFormatted(userFilterStorage?.status),
  startFromDate: userFilterStorage?.start_from_date || "",
  startToDate: userFilterStorage?.start_to_date || "",
  endFromDate: userFilterStorage?.end_from_date_or_null || "",
  endToDate: userFilterStorage?.end_to_date || "",
});

export const EMPTY_MISSION_FILTERS = {
  status: [],
  workers: [],
  clients: [],
  missionTitle: "",
  managers: [],
  startFromDate: "",
  startToDate: "",
  endFromDate: "",
  endToDate: "",
  signedBy: [],
  chargedBy: [],
};

export const getStoredMissionFilters = (stored) => ({
  status: Array.isArray(stored?.status) ? stored.status : [],
  workers: Array.isArray(stored?.workers) ? stored.workers : [],
  clients: Array.isArray(stored?.clients) ? stored.clients : [],
  missionTitle:
    typeof stored?.missionTitle === "string" ? stored.missionTitle : "",
  managers: Array.isArray(stored?.managers) ? stored.managers : [],
  startFromDate:
    typeof stored?.startFromDate === "string" ? stored.startFromDate : "",
  startToDate:
    typeof stored?.startToDate === "string" ? stored.startToDate : "",
  endFromDate:
    typeof stored?.endFromDate === "string" ? stored.endFromDate : "",
  endToDate: typeof stored?.endToDate === "string" ? stored.endToDate : "",
  signedBy: Array.isArray(stored?.signedBy) ? stored.signedBy : [],
  chargedBy: Array.isArray(stored?.chargedBy) ? stored.chargedBy : [],
});

export const hasStoredMissionFilters = (stored) => {
  const f = getStoredMissionFilters(stored);
  return (
    f.status.length > 0 ||
    f.workers.length > 0 ||
    f.clients.length > 0 ||
    f.missionTitle.length > 0 ||
    f.managers.length > 0 ||
    f.startFromDate.length > 0 ||
    f.startToDate.length > 0 ||
    f.endFromDate.length > 0 ||
    f.endToDate.length > 0 ||
    f.signedBy.length > 0 ||
    f.chargedBy.length > 0
  );
};

export const getQueryParamsString = ({ filters }) => {
  const searchParams = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((e) => e && searchParams.append(key, e.value));
    } else {
      value && searchParams.append(key, value);
    }
  });

  const queryString = searchParams.toString().replaceAll("%2C", ",");

  return `&${queryString}`;
};
